import { Calendar, Profile } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import React from 'react';
import { useForm } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import useUser from 'common/hooks/useUser';
import { Typography } from 'common/typography';

import { InsightReportForm, ReportFrequency } from './types';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const HeaderSection = styled(Section)`
  padding: 16px 16px 12px;
  gap: 16px;
  align-self: stretch;
`;

const SchedulesSection = styled(Section)`
  align-self: stretch;
`;

const ScheduleItemContainer = styled(Section)`
  padding: 16px;
  justify-content: center;
`;

const ScheduleItemTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CalendarIcon = styled(Calendar).attrs({
  width: 24,
  height: 24,
})`
  fill: ${cssThemedValue('textPrimary')};
`;

const ScheduleItemDescription = styled.div`
  display: flex;
  padding: 0 32px;
  align-items: flex-start;
  gap: 8px;
  color: ${cssThemedValue('textSecondary')};
`;

const SubscriberSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  padding: 16px;
`;

const ProfileIcon = styled(Profile).attrs({
  width: 24,
  height: 24,
})`
  fill: ${cssThemedValue('textPrimary')};
`;

const InformationSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  padding: 16px;
  color: ${cssThemedValue('textSecondary')};
`;

export const Summary = () => {
  const { user } = useUser();
  const form = useForm<InsightReportForm>();
  const formValues = form.getState().values;
  const { formatMessage } = useIntl();

  const weeklyScheduleItem = {
    title: formatMessage({ id: 'insights.reports.schedules.weekly.title' }),
    description: formatMessage({ id: 'insights.reports.schedules.weekly.description' }),
  };
  const monthlyScheduleItem = {
    title: formatMessage({ id: 'insights.reports.schedules.monthly.title' }),
    description: formatMessage({ id: 'insights.reports.schedules.monthly.description' }),
  };
  const dailyScheduleItem = {
    title: formatMessage({ id: 'insights.reports.schedules.daily.title' }),
    description: formatMessage({ id: 'insights.reports.schedules.daily.description' }),
  };

  const scheduleItems = [
    ...(formValues.reportFrequency?.includes(ReportFrequency.WEEKLY) ? [weeklyScheduleItem] : []),
    ...(formValues.reportFrequency?.includes(ReportFrequency.MONTHLY) ? [monthlyScheduleItem] : []),
    ...(formValues?.reportFrequency?.includes(ReportFrequency.DAILY) ? [dailyScheduleItem] : []),
  ];

  return (
    <>
      <HeaderSection>
        <Typography.Heading6>
          <FormattedMessage id="insights.reports.summary.title" />
        </Typography.Heading6>
      </HeaderSection>
      {scheduleItems.length > 0 && (
        <>
          <HeaderSection>
            <Typography.Heading6>
              <FormattedMessage id="insights.reports.summary.when" />
            </Typography.Heading6>
          </HeaderSection>
          <SchedulesSection>
            {scheduleItems.map(item => (
              <ScheduleItemContainer key={item.title}>
                <ScheduleItemTitle>
                  <CalendarIcon />
                  <Typography.Body3>{item.title}</Typography.Body3>
                </ScheduleItemTitle>
                <ScheduleItemDescription>
                  <Typography.Small>{item.description}</Typography.Small>
                </ScheduleItemDescription>
              </ScheduleItemContainer>
            ))}
          </SchedulesSection>
        </>
      )}
      <HeaderSection>
        <Typography.Heading6>
          <FormattedMessage id="insights.reports.summary.who" />
        </Typography.Heading6>
      </HeaderSection>
      <SubscriberSection>
        <ProfileIcon />
        <Typography.Body3>{user?.email}</Typography.Body3>
      </SubscriberSection>
      <InformationSection>
        <Typography.Small>
          <FormattedMessage id="insights.reports.summary.information" />
        </Typography.Small>
      </InformationSection>
    </>
  );
};
